/** Run and render the bounded prompt × config evaluation matrix. */

import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import type { AgentConfigVersionView } from '@/entities/agentConfig';
import type { AgentPromptVersionView } from '@/entities/agentPrompt';
import { Button } from '@/shared/ui/button';
import { useEvalRunHistory } from '../../../features/settings/evaluation/useEvalRunHistory';
import { useRunBaselineEval } from '../../../features/settings/evaluation/useRunBaselineEval';
import { useSuggestPrompt } from '../../../features/settings/evaluation/useSuggestPrompt';
import ScorecardWidget from './ScorecardWidget';
import SuggestPromptDialog from './SuggestPromptDialog';

// Mirrors MAX_TOTAL_CELLS in src/agenthub/services/eval/constants.py — client-side
// so the cell count updates live as combos/cases change, without a round trip.
const MAX_TOTAL_CELLS = 40;

function manifestFields(version: AgentConfigVersionView | null): Record<string, unknown> {
  const manifest = version?.payload.manifest;
  return typeof manifest === 'object' && manifest !== null ? (manifest as Record<string, unknown>) : {};
}

function usesDbOverlay(version: AgentConfigVersionView | null): boolean {
  return manifestFields(version).use_db_overlay === true;
}

function configDetails(version: AgentConfigVersionView | null) {
  const fields = manifestFields(version);
  const modelField = fields.model;
  const model = typeof modelField === 'string'
    ? modelField
    : typeof modelField === 'object' && modelField !== null
      ? String((modelField as Record<string, unknown>).name ?? 'manifest default')
      : 'manifest default';
  const provider = typeof modelField === 'object' && modelField !== null
    ? String((modelField as Record<string, unknown>).provider ?? 'manifest provider')
    : 'manifest provider';
  return {
    provider,
    model,
    effort: String(fields.effort ?? 'manifest default'),
    maxIterations: String(fields.max_iterations ?? 'manifest default'),
  };
}

export default function EvalRunPanel({
  agentId,
  activeConfig,
  activePrompt,
  draftConfig,
  draftPrompt,
  testCaseCount,
  passThreshold,
  onDraftSaved,
}: {
  agentId: string;
  activeConfig: AgentConfigVersionView | null;
  activePrompt: AgentPromptVersionView | null;
  draftConfig: AgentConfigVersionView | null;
  draftPrompt: AgentPromptVersionView | null;
  testCaseCount: number;
  passThreshold: number | null;
  onDraftSaved?: () => void;
}) {
  const { run, isRunning, error, start, loadRun } = useRunBaselineEval(agentId);
  const { runs: runHistory, reload: reloadHistory } = useEvalRunHistory(agentId);
  const { suggestion, isSuggesting, isSaving, error: suggestError, suggest, saveDraft, reset } =
    useSuggestPrompt(agentId);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [includeDraftPrompt, setIncludeDraftPrompt] = useState(draftPrompt !== null);
  const [includeDraftConfig, setIncludeDraftConfig] = useState(draftConfig !== null);

  useEffect(() => setIncludeDraftPrompt(draftPrompt !== null), [draftPrompt]);
  useEffect(() => setIncludeDraftConfig(draftConfig !== null), [draftConfig]);
  useEffect(() => {
    if (run && (run.status === 'completed' || run.status === 'failed')) reloadHistory();
  }, [run, reloadHistory]);

  const promptVersionIds = useMemo<(string | null)[]>(
    () => includeDraftPrompt && draftPrompt ? [draftPrompt.id, null] : [null],
    [draftPrompt, includeDraftPrompt],
  );
  const configVersionIds = useMemo<(string | null)[]>(
    () => includeDraftConfig && draftConfig ? [draftConfig.id, null] : [null],
    [draftConfig, includeDraftConfig],
  );
  const comboCount = promptVersionIds.length * configVersionIds.length;
  const totalCells = comboCount * testCaseCount;
  const exceedsLimit = totalCells > MAX_TOTAL_CELLS;
  const activeDetails = configDetails(activeConfig);
  const draftDetails = configDetails(draftConfig);

  const hasScores = (run?.results ?? []).some((cell) => cell.status === 'scored');
  const suggestionCombo = run?.combos?.find(
    (combo) => combo.prompt_role === 'candidate' && combo.config_role === 'baseline',
  );
  const candidateHasOptimizationScores = (run?.results ?? []).some(
    (cell) =>
      cell.run_name === suggestionCombo?.run_name &&
      cell.split === 'optimization' &&
      cell.status === 'scored',
  );

  const onSuggest = async () => {
    if (!run || !suggestionCombo) return;
    setDialogOpen(true);
    await suggest(run.id, suggestionCombo.run_name);
  };

  const onSave = async () => {
    if (await saveDraft()) {
      setDialogOpen(false);
      reset();
      toast.success('Draft saved — open the Prompt tab to review and edit it.');
      onDraftSaved?.();
    }
  };

  const closeDialog = () => {
    setDialogOpen(false);
    reset();
  };

  return (
    <section className="flex flex-col gap-3 rounded-lg border border-border bg-card p-3" aria-label="Evaluation matrix">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-lg font-semibold text-foreground">Evaluation matrix</h3>
          <p className="text-sm text-muted-foreground">
            The active prompt and active config are always the control. Add either draft axis or both.
          </p>
        </div>
        <Button
          onClick={() => void start(promptVersionIds, configVersionIds)}
          disabled={isRunning || exceedsLimit || testCaseCount === 0}
        >
          {isRunning ? 'Running…' : 'Run evaluation'}
        </Button>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <div className="rounded border border-border p-3">
          <p className="font-medium text-foreground">
            Active control · prompt {activePrompt ? `v${activePrompt.versionNumber}` : 'manifest'}
          </p>
          <p className="text-sm text-muted-foreground">
            Config {activeConfig?.id === 'manifest' ? 'manifest' : `v${activeConfig?.versionNumber ?? 0}`} ·{' '}
            {activeDetails.provider} / {activeDetails.model} · effort {activeDetails.effort} · max iterations{' '}
            {activeDetails.maxIterations}
          </p>
        </div>
        <div className="flex flex-col gap-2 rounded border border-border p-3">
          <label className="flex items-center gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              checked={includeDraftPrompt}
              disabled={!draftPrompt}
              onChange={(event) => setIncludeDraftPrompt(event.target.checked)}
            />
            Draft prompt {draftPrompt ? `v${draftPrompt.versionNumber}` : '(none)'}
          </label>
          <label className="flex items-start gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              checked={includeDraftConfig}
              disabled={!draftConfig}
              onChange={(event) => setIncludeDraftConfig(event.target.checked)}
            />
            <span>
              Draft config {draftConfig ? `v${draftConfig.versionNumber}` : '(none)'}
              {draftConfig && (
                <span className="block text-muted-foreground">
                  {draftDetails.provider} / {draftDetails.model} · effort {draftDetails.effort} · max iterations{' '}
                  {draftDetails.maxIterations}
                </span>
              )}
              {draftConfig && !usesDbOverlay(draftConfig) && (
                <span className="block text-status-warning">
                  ⚠︎ "Use DB overlay" is off for this config — it will run with manifest defaults instead of the
                  model/effort/max-iterations shown above.
                </span>
              )}
            </span>
          </label>
        </div>
      </div>

      <p className={exceedsLimit ? 'text-sm text-destructive' : 'text-sm text-muted-foreground'}>
        {testCaseCount} cases × {comboCount} combination{comboCount === 1 ? '' : 's'} = {totalCells} cells (maximum{' '}
        {MAX_TOTAL_CELLS}).
      </p>

      {runHistory.length > 0 && (
        <div className="rounded border border-border p-3">
          <p className="mb-2 text-sm font-medium text-foreground">Recent runs</p>
          <ul className="flex flex-col gap-1">
            {runHistory.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => void loadRun(item.id)}
                  disabled={isRunning}
                  className="flex w-full items-center justify-between rounded px-2 py-1 text-left text-sm hover:bg-muted disabled:opacity-50"
                >
                  <span className="text-foreground">
                    {item.status}
                    {item.status === 'failed' && item.error ? ` — ${item.error}` : ''}
                  </span>
                  <span className="text-muted-foreground">
                    {item.progress.terminal}/{item.progress.total} cells
                    {item.created_at ? ` · ${new Date(item.created_at).toLocaleString()}` : ''}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {run && (
        <p className="text-sm text-foreground" aria-live="polite">
          Status: <span className="font-medium">{run.status}</span>
          {run.progress && ` · ${run.progress.terminal}/${run.progress.total} cells finished`}
        </p>
      )}
      {error && <p className="text-sm text-destructive">{error}</p>}
      {run?.status === 'failed' && run.error && <p className="text-sm text-destructive">{run.error}</p>}

      {run?.status === 'completed' && hasScores && (
        <ScorecardWidget run={run} passThreshold={passThreshold} />
      )}

      {run?.status === 'completed' && candidateHasOptimizationScores && suggestionCombo && (
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => void onSuggest()} disabled={isSuggesting}>
            {isSuggesting ? 'Asking Claude…' : 'Suggest prompt improvement'}
          </Button>
          {run.compare_url && (
            <a
              href={run.compare_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-primary underline"
            >
              Open comparison in Langfuse →
            </a>
          )}
        </div>
      )}
      {suggestError && <p className="text-sm text-destructive">{suggestError}</p>}

      {run?.status === 'completed' && !hasScores && (
        <p className="text-sm text-muted-foreground">
          Eval complete, but no cases scored (check the worker logs / model configuration).
        </p>
      )}

      <SuggestPromptDialog
        open={dialogOpen}
        suggestion={suggestion}
        isSuggesting={isSuggesting}
        isSaving={isSaving}
        onSave={() => void onSave()}
        onClose={closeDialog}
      />
    </section>
  );
}
